// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The version ledger — the fix for the sync ping-pong.
 *
 * The File System Access API cannot set a file's modified time, so a file we
 * receive from the peer lands on disk with a *fresh* mtime (~now), not the
 * origin's mtime. If we reported that fresh mtime in our manifest, the peer
 * would think our copy is newer and pull it straight back — an endless loop.
 *
 * So we remember, per synced file, the *version number* that travelled with it
 * (the origin's mtime) and when we wrote it. When we scan, an untouched synced
 * file reports its remembered version, so both sides agree and nothing moves.
 * A file that has since been edited on disk (its size changed, or its mtime is
 * clearly later than when we wrote it) reports its real disk mtime, so the edit
 * propagates normally. The version number travels with the file, so clock skew
 * between the two devices does not cause spurious re-transfers.
 *
 * Pure and unit-tested.
 */
export interface LedgerEntry {
  /** The logical version to report for this path (an mtime value from its origin). */
  version: number;
  /** The plaintext size when we recorded it. */
  size: number;
  /** Local epoch-ms when we wrote/recorded it. */
  writtenAt: number;
}

/** Tolerance (ms) between "wrote it" and the resulting on-disk mtime. */
export const WRITE_SLACK_MS = 4000;

/**
 * Decide the mtime to report in the manifest for a file currently on disk,
 * given what the ledger remembers about it.
 */
export function logicalMtimeFor(
  disk: { size: number; mtime: number },
  entry: LedgerEntry | undefined,
  slackMs: number = WRITE_SLACK_MS,
): number {
  if (entry && disk.size === entry.size && disk.mtime <= entry.writtenAt + slackMs) {
    return entry.version; // untouched synced file → keep its travelled version
  }
  return disk.mtime; // never-synced or locally edited → its own disk mtime is the version
}

/**
 * Whether a disk file looks locally created/edited relative to the ledger — i.e.
 * its reported version should become its own disk mtime and be re-recorded.
 */
export function isLocalEdit(
  disk: { size: number; mtime: number },
  entry: LedgerEntry | undefined,
  slackMs: number = WRITE_SLACK_MS,
): boolean {
  return logicalMtimeFor(disk, entry, slackMs) === disk.mtime;
}
